import React, { createContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react'
import { useAuth } from 'torqcloud-ui'
import NodesApi from '../api/agentBuilder/nodes'
import useApiClient from '../hooks/useApiClient'
import { AvailableNode, AvailableNodeSchema } from '@constants/agentBuilderTypes'
import { apiUrl } from '../utils/http'

interface NodesContextType {
  nodesPool: AvailableNode[]
  loading: boolean
  error: Error | null
  getNodeIconUrl: (nodeName: string) => string
  getNodeByName: (nodeName: string) => AvailableNode | undefined
  refetch: () => Promise<void>
}

export const NodesContext = createContext<NodesContextType | undefined>(undefined)

export const NodesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const [nodesData, setNodesData] = useState<AvailableNode[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const apiClientContext = useApiClient()

  const fetchNodes = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const nodesApi = new NodesApi(apiClientContext.torqAgentClient)
      const response = await nodesApi.getAllNodes()

      // Attempt to parse with schema validation
      const parsedData = AvailableNodeSchema.array().safeParse(response.data)

      if (parsedData.success) {
        setNodesData(parsedData.data)
      } else {
        console.warn('Schema validation failed for nodes data:', parsedData.error)
        // Fall back to raw data if schema validation fails
        setNodesData(response.data)
      }
    } catch (err) {
      setError(err as Error)
      console.error('Failed to fetch nodes:', err)
    } finally {
      setLoading(false)
    }
  }, [apiClientContext.torqAgentClient])

  useEffect(() => {
    if (isAuthenticated) {
      void fetchNodes()
    }
  }, [isAuthenticated, fetchNodes])

  // Memoized helper functions to avoid creating new functions on every render
  const getNodeIconUrl = useMemo(() => (nodeName: string) => apiUrl(`api/v1/nodes/${nodeName}/icon`), [])

  const getNodeByName = useMemo(() => (nodeName: string) => nodesData.find(node => node.name === nodeName), [nodesData])

  const contextValue = useMemo<NodesContextType>(
    () => ({
      nodesPool: nodesData,
      loading,
      error,
      getNodeIconUrl,
      getNodeByName,
      refetch: fetchNodes,
    }),
    [nodesData, loading, error, getNodeIconUrl, getNodeByName, fetchNodes]
  )

  return <NodesContext.Provider value={contextValue}>{children}</NodesContext.Provider>
}
